<script setup>
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const form = reactive({
  inboxName: '',
  phoneNumber: '',
});

const state = ref('close');
const qrcode = ref(null);
const inboxId = ref(null);
const isConnecting = ref(false);

const rules = computed(() => ({
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
}));

const v$ = useVuelidate(rules, form);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const hasQrCode = computed(() => Boolean(qrcode.value?.base64));

const showError = error => {
  useAlert(
    parseAPIErrorResponse(error) ||
      t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.API.ERROR_MESSAGE')
  );
};

const normalizeEvolutionResponse = data => {
  state.value = data?.state || 'close';
  qrcode.value = data?.qrcode || null;
};

const connectInstance = async () => {
  if (!inboxId.value) return;

  isConnecting.value = true;
  try {
    const response = await store.dispatch('inboxes/evolutionConnect', {
      inboxId: inboxId.value,
    });
    normalizeEvolutionResponse(response);
  } catch (error) {
    showError(error);
  } finally {
    isConnecting.value = false;
  }
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const createdInbox = await store.dispatch(
      'inboxes/createEvolutionWhatsAppChannel',
      {
        name: form.inboxName.trim(),
        phoneNumber: form.phoneNumber,
      }
    );

    inboxId.value = createdInbox.id;
    await connectInstance();
  } catch (error) {
    showError(error);
  }
};

const goToAddAgents = () => {
  if (!inboxId.value) return;

  router.replace({
    name: 'settings_inboxes_add_agents',
    params: {
      page: 'new',
      inbox_id: inboxId.value,
    },
  });
};
</script>

<template>
  <div v-if="inboxId" class="flex flex-col gap-6">
    <p class="text-sm text-n-slate-11">
      {{
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONNECTION_STATE', {
          state: state,
        })
      }}
    </p>

    <div
      v-if="hasQrCode"
      class="flex flex-col gap-3 items-center rounded-2xl border border-n-weak p-6"
    >
      <img
        :src="qrcode.base64"
        :alt="$t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_ALT')"
        class="size-64 max-w-full object-contain"
      />
      <p class="mb-0 text-sm text-center text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.QR_HELP_TEXT') }}
      </p>
      <p
        v-if="qrcode.pairing_code"
        class="mb-0 text-xs text-center text-n-slate-10"
      >
        {{
          $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.PAIRING_CODE', {
            code: qrcode.pairing_code,
          })
        }}
      </p>
    </div>

    <div
      v-else
      class="rounded-2xl border border-n-weak p-6 text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.NO_QR_AVAILABLE') }}
    </div>

    <div class="flex gap-3 items-center">
      <NextButton :is-loading="isConnecting" @click="connectInstance">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.GENERATE_NEW_QR') }}
      </NextButton>
      <NextButton solid blue @click="goToAddAgents">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.CONTINUE_BUTTON') }}
      </NextButton>
    </div>
  </div>

  <form
    v-else
    class="flex flex-wrap flex-col mx-0"
    @submit.prevent="createChannel()"
  >
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="form.inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="form.phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.INSTANCE_NAME.AUTO_HELP') }}
      </p>
    </div>

    <div class="w-full mt-4">
      <NextButton
        :is-loading="uiFlags.isCreating"
        type="submit"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
